import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  type Auth,
} from 'firebase/auth';
import {
  initializeFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from 'firebase/storage';
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from 'firebase/functions';

const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';
const apiKeyFromEnv = import.meta.env.VITE_FIREBASE_API_KEY;

/** Real Identity Toolkit rejects `demo-api-key`; emulators accept it. */
if (!useEmulators) {
  const missingOrDemo =
    !apiKeyFromEnv ||
    String(apiKeyFromEnv).trim() === '' ||
    apiKeyFromEnv === 'demo-api-key';
  if (missingOrDemo) {
    throw new Error(
      'Firebase is not configured: set VITE_FIREBASE_API_KEY in web/.env.local (copy from web/.env.example and paste values from Firebase Console → Project settings → Your apps). ' +
        'Or use local emulators: set VITE_USE_EMULATORS=true in web/.env.local and run firebase emulators:start from the repo root.',
    );
  }
}

const firebaseConfig = {
  apiKey: apiKeyFromEnv ?? 'demo-api-key',
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? 'demo.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? 'chhabighar-47f08',
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? 'chhabighar-47f08.appspot.com',
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '000000000000',
  appId:
    import.meta.env.VITE_FIREBASE_APP_ID ??
    '1:000000000000:web:0000000000000000000000',
};

const app: FirebaseApp = getApps()[0] ?? initializeApp(firebaseConfig);

export const auth: Auth = getAuth(app);
// Offline persistence: loads cached data instantly on repeat visits while
// Firestore checks the server for updates in the background. Multi-tab
// manager keeps multiple open tabs sharing one cache instead of fighting
// over it.
export const db: Firestore = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});
export const storage: FirebaseStorage = getStorage(app);
export const functions: Functions = getFunctions(app, 'us-central1');

if (useEmulators && typeof window !== 'undefined') {
  // Idempotent guards
  // @ts-expect-error mark on window
  if (!window.__firebaseEmulatorsConnected) {
    try {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      connectFirestoreEmulator(db, 'localhost', 8080);
      connectStorageEmulator(storage, 'localhost', 9199);
      connectFunctionsEmulator(functions, 'localhost', 5001);
      // @ts-expect-error mark on window
      window.__firebaseEmulatorsConnected = true;
    } catch (err) {
      console.warn('Failed to connect to emulators', err);
    }
  }
}

export { app };
